import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { GALLERY } from "@/data/content";

export const Gallery = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: targetRef });
  const x = useTransform(scrollYProgress, [0, 1], ["2%", "-72%"]);
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const step = useCallback((dir) => {
    setActive((i) => (i === null ? i : (i + dir + GALLERY.length) % GALLERY.length));
  }, []);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, step]);

  return (
    <>
      <section ref={targetRef} className="relative h-[300vh] bg-charcoal" data-testid="gallery-section">
        <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
          <div className="mx-auto max-w-[1400px] w-full px-6 md:px-12 mb-10 md:mb-14">
            <p className="overline text-champagne mb-5">Signature Homes</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.08] text-warmwhite max-w-2xl">
              The spaces we're trusted to care for.
            </h2>
          </div>

          <motion.div style={{ x }} className="flex gap-6 md:gap-8 pl-6 md:pl-12 will-change-transform">
            {GALLERY.map((g, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className="relative shrink-0 w-[78vw] sm:w-[52vw] lg:w-[34vw] group text-left"
                data-testid={`gallery-item-${i}`}
                aria-label={`View ${g.label}`}
              >
                <div className="img-frame">
                  <img src={g.src} alt={g.label} className="w-full h-[52vh] md:h-[60vh] object-cover" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-between">
                  <span className="font-serif italic text-warmwhite text-2xl md:text-3xl">{g.label}</span>
                  <span className="overline text-warmwhite/0 group-hover:text-warmwhite/80 transition-colors duration-300 mb-1">View</span>
                </div>
              </button>
            ))}
            <div className="shrink-0 w-[20vw]" />
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[110] bg-charcoal/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={close}
              className="absolute top-6 right-6 md:top-8 md:right-10 text-warmwhite/70 hover:text-warmwhite text-4xl leading-none z-10"
              data-testid="lightbox-close"
              aria-label="Close"
            >
              ×
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-4 md:left-10 text-warmwhite/60 hover:text-champagne text-3xl md:text-5xl z-10 transition-colors"
              data-testid="lightbox-prev"
              aria-label="Previous"
            >
              ‹
            </button>

            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-6xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[active].src}
                alt={GALLERY[active].label}
                className="w-full max-h-[80vh] object-contain"
                data-testid="lightbox-image"
              />
              <figcaption className="mt-5 flex items-center justify-between">
                <span className="font-serif italic text-warmwhite text-2xl md:text-3xl">{GALLERY[active].label}</span>
                <span className="overline text-warmwhite/40">{active + 1} / {GALLERY.length}</span>
              </figcaption>
            </motion.figure>

            <button
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-4 md:right-10 text-warmwhite/60 hover:text-champagne text-3xl md:text-5xl z-10 transition-colors"
              data-testid="lightbox-next"
              aria-label="Next"
            >
              ›
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
